import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { isRateLimitError } from "#/features/trade/lib/hyperliquid-error.ts";
import { getClientTolgee } from "#/integrations/tolgee/tolgee-instance.ts";

const TOAST_ID = "hl-rate-limit";

// sentry.ts beforeSend drops raw 429 *error* events as user-visible-recovered
// noise, which left the rate-limit guardrail unobservable. This warning message
// is the countable signal that survives that filter: one grouped issue
// (stable fingerprint), throttled to one event per interval, with the number of
// throttled occurrences attached so burst magnitude isn't lost.
const REPORT_INTERVAL_MS = 60_000;
let lastReportAt = 0;
let suppressedSinceReport = 0;

/**
 * Count a Hyperliquid 429 in Sentry (throttled). Safe no-op when Sentry is not
 * initialized (dev, automated browsers, missing DSN). Call directly from retry/backoff
 * paths that intentionally swallow the error without toasting.
 */
export function reportRateLimit(): void {
	const now = Date.now();
	if (now - lastReportAt < REPORT_INTERVAL_MS) {
		suppressedSinceReport += 1;
		return;
	}
	Sentry.captureMessage("hyperliquid rate limit (429)", {
		level: "warning",
		tags: { guardrail: "rate_limit" },
		extra: { suppressed_since_last_report: suppressedSinceReport },
		fingerprint: ["hyperliquid-rate-limit"],
	});
	lastReportAt = now;
	suppressedSinceReport = 0;
}

export function notifyIfRateLimit(error: unknown): boolean {
	if (!isRateLimitError(error)) return false;

	reportRateLimit();
	toast.error(
		getClientTolgee().t({
			key: "toast.rate-limit",
			defaultValue: "Too many requests. Please wait a moment and try again.",
		}),
		{ id: TOAST_ID, duration: 4000 },
	);

	return true;
}

/** Test-only: reset the report throttle state. */
export function __resetRateLimitReportForTest(): void {
	lastReportAt = 0;
	suppressedSinceReport = 0;
}
